import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Fab,
  Grid,
  FormControlLabel,
  Switch,
  Stack,
  Chip,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Park as ParkIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  UploadFile as UploadFileIcon,
  ExpandMore as ExpandMoreIcon,
  DeleteForever as DeleteForeverIcon,
  Publish as PublishIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';
import { dogGardensApi } from '../services/api';
import LocationPickerMap from './LocationPickerMap';

const DogGardens = () => {
  const navigate = useNavigate();
  const [gardens, setGardens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentGarden, setCurrentGarden] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    country: '',
    address: '',
    latitude: '',
    longitude: '',
    rating: null,
    userRatingTotal: null,
    placeId: '',
    parkAmenitiesActivityStatus: [],
    visible: false
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [gardenToDelete, setGardenToDelete] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sorting state
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('ASC');

  // Pending flag
  const [onlyInvisible, setOnlyInvisible] = useState(false);

  // Search inputs (draft)
  const [filterQuery,   setFilterQuery]   = useState('');
  const [filterCity,    setFilterCity]    = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterSource,  setFilterSource]  = useState(''); // '' | 'SYSTEM' | 'USER'

  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState({ query: '', city: '', country: '', source: '' });

  const isSearchActive = appliedFilters.query || appliedFilters.city || appliedFilters.country || appliedFilters.source;

  // Removal reports modal state
  const [reportsModalGarden, setReportsModalGarden] = useState(null);
  const [reportsModalReports, setReportsModalReports] = useState([]);
  const [reportsModalLoading, setReportsModalLoading] = useState(false);
  const [reportsModalError, setReportsModalError] = useState('');

  // {gardenId -> openReportCount} loaded once per page render (cheap, only includes gardens
  // with at least one open report). Drives the "Reports" badge column.
  const [openReportCounts, setOpenReportCounts] = useState({});

  // Bulk hide dialog state
  const [bulkHideOpen, setBulkHideOpen] = useState(false);
  const [bulkHideCountry, setBulkHideCountry] = useState(null);
  const [bulkHideCity, setBulkHideCity] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [bulkHidePreview, setBulkHidePreview] = useState(null);
  const [bulkHideLoading, setBulkHideLoading] = useState(false);
  const [bulkHideError, setBulkHideError] = useState('');

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importRadius, setImportRadius] = useState(100);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');

  // Bulk delete dialog state
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteCountry, setBulkDeleteCountry] = useState(null);
  const [bulkDeleteCity, setBulkDeleteCity] = useState(null);
  const [bulkDeletePreview, setBulkDeletePreview] = useState(null);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState('');
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');

  // Bulk publish dialog state
  const [bulkPublishOpen, setBulkPublishOpen] = useState(false);
  const [bulkPublishCountry, setBulkPublishCountry] = useState(null);
  const [bulkPublishCity, setBulkPublishCity] = useState(null);
  const [bulkPublishPreview, setBulkPublishPreview] = useState(null);
  const [bulkPublishLoading, setBulkPublishLoading] = useState(false);
  const [bulkPublishError, setBulkPublishError] = useState('');

  const fetchGardens = useCallback(async (pageNum = page, pageSize = rowsPerPage, sort = sortBy, direction = sortDirection) => {
    try {
      setLoading(true);
      let data;
      if (isSearchActive || onlyInvisible) {
        data = await dogGardensApi.adminSearchGardens({
          query:   appliedFilters.query   || undefined,
          city:    appliedFilters.city    || undefined,
          country: appliedFilters.country || undefined,
          visible: onlyInvisible ? false : undefined,
          source:  appliedFilters.source  || undefined,
          page: pageNum,
          size: pageSize,
          sort,
          direction,
        });
      } else {
        data = await dogGardensApi.getAllGardensPaginated(pageNum, pageSize, sort, direction, null);
      }
      setGardens(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      setError('');
    } catch (err) {
      console.error('Error fetching dog gardens:', err);
      setError('Failed to load dog gardens. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortDirection, onlyInvisible, appliedFilters, isSearchActive]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      query:   filterQuery.trim(),
      city:    filterCity.trim(),
      country: filterCountry.trim(),
      source:  filterSource,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilterQuery('');
    setFilterCity('');
    setFilterCountry('');
    setFilterSource('');
    setAppliedFilters({ query: '', city: '', country: '', source: '' });
    setPage(0);
  };

  const handleViewRemovalReports = useCallback(async (garden) => {
    setReportsModalGarden(garden);
    setReportsModalReports([]);
    setReportsModalError('');
    setReportsModalLoading(true);
    try {
      const reports = await dogGardensApi.getRemovalReports(garden.id);
      setReportsModalReports(reports || []);
    } catch (err) {
      console.error('Error loading removal reports:', err);
      setReportsModalError('Failed to load removal reports.');
    } finally {
      setReportsModalLoading(false);
    }
  }, []);

  const handleCloseRemovalReports = () => {
    setReportsModalGarden(null);
    setReportsModalReports([]);
    setReportsModalError('');
  };

  // Handle column sort
  const handleSort = (column) => {
    const isAsc = sortBy === column && sortDirection === 'ASC';
    const newDirection = isAsc ? 'DESC' : 'ASC';
    setSortDirection(newDirection);
    setSortBy(column);
    fetchGardens(page, rowsPerPage, column, newDirection);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchGardens();
  }, [fetchGardens]);

  // Load open-report counts in parallel with gardens. Non-blocking — if it fails the
  // gardens list still renders, just without the badge column populated.
  useEffect(() => {
    let cancelled = false;
    dogGardensApi.getOpenRemovalReportCounts()
      .then((counts) => {
        if (!cancelled) setOpenReportCounts(counts || {});
      })
      .catch((err) => {
        console.error('Failed to load open removal report counts:', err);
      });
    return () => { cancelled = true; };
  }, [page, rowsPerPage, appliedFilters, onlyInvisible]);

  const handleEditClick = (garden) => {
    setCurrentGarden(garden);
    setFormData({
      name: garden.gardenName,
      city: garden.cityDto?.nameEn ?? garden.cityDto?.nameHe ?? garden.cityName ?? '',
      country: garden.countryDto?.nameEn ?? garden.countryDto?.nameHe ?? garden.countryName ?? '',
      address: garden.address,
      latitude: garden.latitude,
      longitude: garden.longitude,
      rating: garden.rating,
      userRatingTotal: garden.userRatingTotal,
      placeId: garden.placeId || '',
      parkAmenitiesActivityStatus: garden.parkAmenitiesActivityStatus || [],
      visible: garden.visible
    });
    setOpenDialog(true);
  };

  const handleCreateClick = () => {
    setCurrentGarden(null);
    setFormData({
      name: '',
      city: '',
      country: '',
      address: '',
      latitude: '',
      longitude: '',
      rating: null,
      userRatingTotal: null,
      placeId: '',
      parkAmenitiesActivityStatus: [],
      visible: true,
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (garden) => {
    setGardenToDelete(garden);
    setDeleteConfirmOpen(true);
  };

  const handleViewDetails = (gardenId) => {
    navigate(`/dog-gardens/${gardenId}`);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentGarden(null);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setGardenToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSaveGarden = async () => {
    try {
      setLoading(true);

      if (currentGarden) {
        // Prepare data with correct property names
        const gardenData = {
          gardenName: formData.name,
          cityName: formData.city || null,
          countryName: formData.country || null,
          address: formData.address,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          rating: formData.rating,
          userRatingTotal: formData.userRatingTotal,
          placeId: formData.placeId,
          parkAmenitiesActivityStatus: formData.parkAmenitiesActivityStatus,
          visible: formData.visible
        };

        // Update existing garden
        await dogGardensApi.updateGarden(currentGarden.id, gardenData);

        // Refresh the current page to get updated data
        fetchGardens(page);

        setSuccessMessage(`Dog garden "${formData.name}" has been updated.`);
      } else {
        // Create new garden
        const gardenData = {
          gardenName: formData.name,
          cityName: formData.city || null,
          countryName: formData.country || null,
          address: formData.address,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          placeId: formData.placeId || null,
          visible: formData.visible,
        };

        await dogGardensApi.createGarden(gardenData);

        // Refresh to show the newly created garden
        fetchGardens(page);

        setSuccessMessage(`Dog garden "${formData.name}" has been created.`);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      const action = currentGarden ? 'update' : 'create';
      console.error(`Error trying to ${action} dog garden:`, err);
      setError(`Failed to ${action} dog garden: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
      handleCloseDialog();
    }
  };

  const handleDeleteGarden = async () => {
    try {
      setLoading(true);

      // Delete garden from the backend
      await dogGardensApi.deleteGarden(gardenToDelete.id);

      // Refresh the current page to get updated data
      // If we're on the last page and it's now empty, go to the previous page
      const currentPageItemCount = gardens.length;
      if (currentPageItemCount === 1 && page > 0) {
        // If this was the last item on the page, go to previous page
        setPage(page - 1);
        fetchGardens(page - 1);
      } else {
        // Otherwise, refresh the current page
        fetchGardens(page);
      }

      setSuccessMessage(`Dog garden "${gardenToDelete.gardenName}" has been deleted.`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error deleting dog garden:', err);
      setError(`Failed to delete dog garden: ${err.message}`);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setLoading(false);
      handleCloseDeleteConfirm();
    }
  };

  // ── Bulk hide handlers ──

  const fetchCountries = useCallback(async () => {
    try {
      const list = await dogGardensApi.getDistinctCountries();
      setCountryOptions(list || []);
    } catch (e) {
      console.error('Failed to load countries', e);
    }
  }, []);

  const fetchCities = useCallback(async (country) => {
    try {
      const list = await dogGardensApi.getDistinctCities(country || null);
      setCityOptions(list || []);
    } catch (e) {
      console.error('Failed to load cities', e);
    }
  }, []);

  const handleOpenBulkHide = () => {
    setBulkHideOpen(true);
    setBulkHideCountry(null);
    setBulkHideCity(null);
    setBulkHidePreview(null);
    setBulkHideError('');
    fetchCountries();
    fetchCities(null);
  };

  const handleCloseBulkHide = () => {
    if (bulkHideLoading) return;
    setBulkHideOpen(false);
  };

  const handlePreviewBulkHide = async () => {
    if (!bulkHideCity && !bulkHideCountry) {
      setBulkHideError('Provide a city, a country, or both.');
      return;
    }
    setBulkHideError('');
    setBulkHideLoading(true);
    try {
      const result = await dogGardensApi.previewBulkHide({
        city: bulkHideCity || undefined,
        country: bulkHideCountry || undefined,
      });
      setBulkHidePreview(result);
    } catch (e) {
      setBulkHideError(`Preview failed: ${e.message}`);
    } finally {
      setBulkHideLoading(false);
    }
  };

  const handleConfirmBulkHide = async () => {
    setBulkHideLoading(true);
    try {
      const result = await dogGardensApi.bulkHide({
        city: bulkHideCity || undefined,
        country: bulkHideCountry || undefined,
      });
      setSuccessMessage(`${result.affectedCount} garden(s) have been hidden.`);
      setTimeout(() => setSuccessMessage(''), 4000);
      setBulkHideOpen(false);
      fetchGardens(page);
    } catch (e) {
      setBulkHideError(`Hide failed: ${e.message}`);
    } finally {
      setBulkHideLoading(false);
    }
  };

  // ── Import handlers ──

  const handleOpenImport = () => {
    setImportOpen(true);
    setImportFile(null);
    setImportResult(null);
    setImportError('');
    setImportRadius(100);
  };

  const handleCloseImport = () => {
    if (importLoading) return;
    setImportOpen(false);
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    setImportFile(f || null);
    setImportResult(null);
    setImportError('');
  };

  const handleSubmitImport = async () => {
    if (!importFile) {
      setImportError('Please select a JSON file.');
      return;
    }
    setImportError('');
    setImportLoading(true);
    try {
      const result = await dogGardensApi.bulkImportFromJson(importFile, importRadius);
      setImportResult(result);
      fetchGardens(page);
    } catch (e) {
      setImportError(e.message);
    } finally {
      setImportLoading(false);
    }
  };

  // ── Bulk delete handlers ──

  const handleOpenBulkDelete = () => {
    setBulkDeleteOpen(true);
    setBulkDeleteCountry(null);
    setBulkDeleteCity(null);
    setBulkDeletePreview(null);
    setBulkDeleteResult(null);
    setBulkDeleteError('');
    setBulkDeleteConfirmText('');
    fetchCountries();
    fetchCities(null);
  };

  const handleCloseBulkDelete = () => {
    if (bulkDeleteLoading) return;
    setBulkDeleteOpen(false);
  };

  const handlePreviewBulkDelete = async () => {
    if (!bulkDeleteCity && !bulkDeleteCountry) {
      setBulkDeleteError('Provide a city, a country, or both.');
      return;
    }
    setBulkDeleteError('');
    setBulkDeleteLoading(true);
    try {
      const result = await dogGardensApi.previewBulkHide({
        city: bulkDeleteCity || undefined,
        country: bulkDeleteCountry || undefined,
      });
      setBulkDeletePreview(result);
    } catch (e) {
      setBulkDeleteError(`Preview failed: ${e.message}`);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleConfirmBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      const result = await dogGardensApi.bulkDelete({
        city: bulkDeleteCity || undefined,
        country: bulkDeleteCountry || undefined,
      });
      setBulkDeleteResult(result);
      if (result.skipped?.length === 0) {
        setSuccessMessage(`${result.deletedCount} garden(s) deleted.`);
        setTimeout(() => setSuccessMessage(''), 4000);
      }
      fetchGardens(page);
    } catch (e) {
      setBulkDeleteError(`Delete failed: ${e.message}`);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // ── Bulk publish handlers ──

  const handleOpenBulkPublish = () => {
    setBulkPublishOpen(true);
    setBulkPublishCountry(null);
    setBulkPublishCity(null);
    setBulkPublishPreview(null);
    setBulkPublishError('');
    fetchCountries();
    fetchCities(null);
  };

  const handleCloseBulkPublish = () => {
    if (bulkPublishLoading) return;
    setBulkPublishOpen(false);
  };

  const handlePreviewBulkPublish = async () => {
    if (!bulkPublishCity && !bulkPublishCountry) {
      setBulkPublishError('Provide a city, a country, or both.');
      return;
    }
    setBulkPublishError('');
    setBulkPublishLoading(true);
    try {
      const result = await dogGardensApi.previewBulkPublish({
        city: bulkPublishCity || undefined,
        country: bulkPublishCountry || undefined,
      });
      setBulkPublishPreview(result);
    } catch (e) {
      setBulkPublishError(`Preview failed: ${e.message}`);
    } finally {
      setBulkPublishLoading(false);
    }
  };

  const handleConfirmBulkPublish = async () => {
    setBulkPublishLoading(true);
    try {
      const result = await dogGardensApi.bulkPublish({
        city: bulkPublishCity || undefined,
        country: bulkPublishCountry || undefined,
      });
      setSuccessMessage(`${result.affectedCount} garden(s) have been published.`);
      setTimeout(() => setSuccessMessage(''), 4000);
      setBulkPublishOpen(false);
      fetchGardens(page);
    } catch (e) {
      setBulkPublishError(`Publish failed: ${e.message}`);
    } finally {
      setBulkPublishLoading(false);
    }
  };

  if (loading && gardens.length === 0) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1">
          Dog Gardens Management
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="success"
            startIcon={<PublishIcon />}
            onClick={handleOpenBulkPublish}
          >
            Bulk Publish
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<VisibilityOffIcon />}
            onClick={handleOpenBulkHide}
          >
            Bulk Hide
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={handleOpenBulkDelete}
          >
            Bulk Delete
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenImport}
          >
            Import JSON
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Create New Garden
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search / filter bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
            <TextField
              label="Search name or address"
              placeholder="e.g. Central Park"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              size="small"
              sx={{ minWidth: 240 }}
            />
            <TextField
              label="City"
              placeholder="e.g. Tel Aviv"
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              size="small"
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="Country"
              placeholder="e.g. Israel"
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              size="small"
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="source-filter-label">Source</InputLabel>
              <Select
                labelId="source-filter-label"
                label="Source"
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SYSTEM">System</MenuItem>
                <MenuItem value="USER">User</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={onlyInvisible}
                  onChange={(e) => { setOnlyInvisible(e.target.checked); setPage(0); }}
                  color="warning"
                />
              }
              label="Pending only"
              sx={{ whiteSpace: 'nowrap' }}
            />
            <Button variant="contained" onClick={handleApplyFilters}>Search</Button>
            {(isSearchActive || onlyInvisible) && (
              <Button variant="outlined" onClick={handleClearFilters}>Clear</Button>
            )}
          </Stack>
          {isSearchActive && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {appliedFilters.query   && <Chip size="small" label={`Text: ${appliedFilters.query}`}     onDelete={() => { setFilterQuery('');   setAppliedFilters(f => ({ ...f, query: '' }));   setPage(0); }} />}
              {appliedFilters.city    && <Chip size="small" label={`City: ${appliedFilters.city}`}       onDelete={() => { setFilterCity('');    setAppliedFilters(f => ({ ...f, city: '' }));    setPage(0); }} />}
              {appliedFilters.country && <Chip size="small" label={`Country: ${appliedFilters.country}`} onDelete={() => { setFilterCountry(''); setAppliedFilters(f => ({ ...f, country: '' })); setPage(0); }} />}
              {appliedFilters.source  && <Chip size="small" label={`Source: ${appliedFilters.source}`}   onDelete={() => { setFilterSource('');  setAppliedFilters(f => ({ ...f, source: '' }));  setPage(0); }} />}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="gardens table">
            <TableHead>
              <TableRow>
                <TableCell 
                  onClick={() => handleSort('id')}
                  sx={{ cursor: 'pointer', fontWeight: sortBy === 'id' ? 'bold' : 'normal' }}
                >
                  ID {sortBy === 'id' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('gardenName')}
                  sx={{ cursor: 'pointer', fontWeight: sortBy === 'gardenName' ? 'bold' : 'normal' }}
                >
                  Name {sortBy === 'gardenName' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Address</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Coordinates</TableCell>
                <TableCell>Visible</TableCell>
                <TableCell>Reports</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Submitted by</TableCell>
                <TableCell
                  onClick={() => handleSort('createdAt')}
                  sx={{ cursor: 'pointer', fontWeight: sortBy === 'createdAt' ? 'bold' : 'normal' }}
                >
                  Created Date {sortBy === 'createdAt' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('updatedAt')}
                  sx={{ cursor: 'pointer', fontWeight: sortBy === 'updatedAt' ? 'bold' : 'normal' }}
                >
                  Updated Date {sortBy === 'updatedAt' && (sortDirection === 'ASC' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gardens.map((garden) => (
                <TableRow key={garden.id}>
                  <TableCell>{garden.id}</TableCell>
                  <TableCell>{garden.gardenName}</TableCell>
                  <TableCell>{garden.address}</TableCell>
                  <TableCell>{garden.cityDto?.nameEn ?? garden.cityDto?.nameHe ?? garden.cityName ?? '-'}</TableCell>
                  <TableCell>{garden.countryDto?.nameEn ?? garden.countryDto?.nameHe ?? garden.countryName ?? '-'}</TableCell>
                  <TableCell>
                    {garden.latitude != null && garden.longitude != null ? (
                      <Tooltip title="Open in Google Maps">
                        <Link
                          href={`https://www.google.com/maps/search/?api=1&query=${garden.latitude},${garden.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <LocationIcon fontSize="inherit" />
                          {garden.latitude}, {garden.longitude}
                        </Link>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{garden.visible ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    {openReportCounts[garden.id] ? (
                      <Tooltip title="Open removal reports — click the ⚠️ action to view">
                        <Chip
                          size="small"
                          color="warning"
                          icon={<ReportProblemIcon />}
                          label={openReportCounts[garden.id]}
                          onClick={() => handleViewRemovalReports(garden)}
                          clickable
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {garden.source ? (
                      <Chip
                        size="small"
                        label={garden.source}
                        color={garden.source === 'USER' ? 'primary' : 'default'}
                        variant={garden.source === 'USER' ? 'filled' : 'outlined'}
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {garden.submittedBy ? garden.submittedBy.name : '-'}
                  </TableCell>
                  <TableCell>
                    {garden.createdAt ? new Date(garden.createdAt).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    {garden.updatedAt ? new Date(garden.updatedAt).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleViewDetails(garden.id)}
                      title="View Details"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(garden)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => handleViewRemovalReports(garden)}
                      title="View removal reports"
                    >
                      <ReportProblemIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(garden)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Dog Gardens Management - Connected to backend API
        </Typography>
      </Box>

      {/* Floating action button for mobile */}
      <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'fixed', bottom: 16, right: 16 }}>
        <Fab color="primary" aria-label="add" onClick={handleCreateClick}>
          <AddIcon />
        </Fab>
      </Box>

      {/* Edit Garden Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{currentGarden ? 'Edit Dog Garden' : 'Create New Garden'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="City"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ step: 'any' }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ step: 'any' }}
                  required
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 1 }}>
              <LocationPickerMap
                latitude={formData.latitude}
                longitude={formData.longitude}
                onLocationChange={(lat, lng) => setFormData((prev) => ({
                  ...prev,
                  latitude: lat.toFixed(6),
                  longitude: lng.toFixed(6),
                }))}
              />
            </Box>
            <TextField
              fullWidth
              margin="normal"
              label="Place ID"
              name="placeId"
              value={formData.placeId}
              onChange={handleInputChange}
              helperText="Google Maps Place ID"
            />
            <TextField
              fullWidth
              margin="normal"
              label="Rating"
              name="rating"
              value={formData.rating || ''}
              onChange={handleInputChange}
              type="number"
              inputProps={{ step: 'any', min: 0, max: 5 }}
              helperText="Rating from 0 to 5"
              disabled
            />
            <TextField
              fullWidth
              margin="normal"
              label="User Rating Total"
              name="userRatingTotal"
              value={formData.userRatingTotal || ''}
              onChange={handleInputChange}
              type="number"
              inputProps={{ min: 0 }}
              helperText="Total number of user ratings"
              disabled
            />
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Park Amenities Activity Status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This field is managed by the backend system.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.visible}
                  onChange={(e) => setFormData({...formData, visible: e.target.checked})}
                  name="visible"
                  color="primary"
                />
              }
              label="Visible"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveGarden} variant="contained" color="primary">
            {currentGarden ? 'Save Changes' : 'Create Garden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the dog garden "{gardenToDelete?.gardenName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteGarden} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Hide Dialog */}
      <Dialog open={bulkHideOpen} onClose={handleCloseBulkHide} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Hide Gardens</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Hide all gardens matching the selected filter. This sets <code>visible=false</code>
              and is reversible — no rows are deleted. At least one filter is required.
            </Typography>
            <Autocomplete
              freeSolo
              options={countryOptions}
              value={bulkHideCountry}
              onChange={(_, v) => {
                setBulkHideCountry(v || null);
                setBulkHideCity(null);
                setBulkHidePreview(null);
                fetchCities(v || null);
              }}
              onInputChange={(_, v) => setBulkHideCountry(v || null)}
              renderInput={(p) => <TextField {...p} label="Country" margin="normal" fullWidth />}
            />
            <Autocomplete
              freeSolo
              options={cityOptions}
              value={bulkHideCity}
              onChange={(_, v) => {
                setBulkHideCity(v || null);
                setBulkHidePreview(null);
              }}
              onInputChange={(_, v) => setBulkHideCity(v || null)}
              renderInput={(p) => <TextField {...p} label="City" margin="normal" fullWidth />}
            />
            {bulkHidePreview && (
              <Alert severity={bulkHidePreview.count === 0 ? 'info' : 'warning'} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                  {bulkHidePreview.count} garden(s) will be hidden.
                </Typography>
                {bulkHidePreview.sampleNames?.length > 0 && (
                  <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                    Sample: {bulkHidePreview.sampleNames.join(', ')}
                    {bulkHidePreview.count > bulkHidePreview.sampleNames.length && ' …'}
                  </Typography>
                )}
              </Alert>
            )}
            {bulkHideError && <Alert severity="error" sx={{ mt: 2 }}>{bulkHideError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkHide} disabled={bulkHideLoading}>Cancel</Button>
          <Button
            onClick={handlePreviewBulkHide}
            disabled={bulkHideLoading || (!bulkHideCity && !bulkHideCountry)}
          >
            Preview
          </Button>
          <Button
            onClick={handleConfirmBulkHide}
            variant="contained"
            color="warning"
            disabled={bulkHideLoading || !bulkHidePreview || bulkHidePreview.count === 0}
            startIcon={bulkHideLoading ? <CircularProgress size={16} /> : <VisibilityOffIcon />}
          >
            Confirm Hide
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Publish Dialog */}
      <Dialog open={bulkPublishOpen} onClose={handleCloseBulkPublish} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'success.main' }}>Bulk Publish Gardens</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Make all currently-hidden gardens matching the filter visible to users.
              Already-visible gardens are not counted or changed. Useful for promoting
              batch-imported gardens after review.
            </Typography>
            <Autocomplete
              freeSolo
              options={countryOptions}
              value={bulkPublishCountry}
              onChange={(_, v) => {
                setBulkPublishCountry(v || null);
                setBulkPublishCity(null);
                setBulkPublishPreview(null);
                fetchCities(v || null);
              }}
              onInputChange={(_, v) => setBulkPublishCountry(v || null)}
              renderInput={(p) => <TextField {...p} label="Country" margin="normal" fullWidth />}
            />
            <Autocomplete
              freeSolo
              options={cityOptions}
              value={bulkPublishCity}
              onChange={(_, v) => {
                setBulkPublishCity(v || null);
                setBulkPublishPreview(null);
              }}
              onInputChange={(_, v) => setBulkPublishCity(v || null)}
              renderInput={(p) => <TextField {...p} label="City" margin="normal" fullWidth />}
            />
            {bulkPublishPreview && (
              <Alert
                severity={bulkPublishPreview.count === 0 ? 'info' : 'success'}
                sx={{ mt: 2 }}
              >
                <Typography variant="subtitle2">
                  {bulkPublishPreview.count} hidden garden(s) will be published.
                </Typography>
                {bulkPublishPreview.sampleNames?.length > 0 && (
                  <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                    Sample: {bulkPublishPreview.sampleNames.join(', ')}
                    {bulkPublishPreview.count > bulkPublishPreview.sampleNames.length && ' …'}
                  </Typography>
                )}
              </Alert>
            )}
            {bulkPublishError && <Alert severity="error" sx={{ mt: 2 }}>{bulkPublishError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkPublish} disabled={bulkPublishLoading}>Cancel</Button>
          <Button
            onClick={handlePreviewBulkPublish}
            disabled={bulkPublishLoading || (!bulkPublishCity && !bulkPublishCountry)}
          >
            Preview
          </Button>
          <Button
            onClick={handleConfirmBulkPublish}
            variant="contained"
            color="success"
            disabled={bulkPublishLoading || !bulkPublishPreview || bulkPublishPreview.count === 0}
            startIcon={bulkPublishLoading ? <CircularProgress size={16} /> : <PublishIcon />}
          >
            Confirm Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteOpen} onClose={handleCloseBulkDelete} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Bulk Delete Gardens</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>This permanently deletes gardens.</strong> Gardens still referenced
                by posts, meet events, followers, dogs, or notifications will be skipped
                automatically and reported below. Use "Bulk Hide" instead if you want a
                reversible operation.
              </Typography>
            </Alert>
            <Autocomplete
              freeSolo
              options={countryOptions}
              value={bulkDeleteCountry}
              onChange={(_, v) => {
                setBulkDeleteCountry(v || null);
                setBulkDeleteCity(null);
                setBulkDeletePreview(null);
                setBulkDeleteResult(null);
                fetchCities(v || null);
              }}
              onInputChange={(_, v) => setBulkDeleteCountry(v || null)}
              renderInput={(p) => <TextField {...p} label="Country" margin="normal" fullWidth />}
            />
            <Autocomplete
              freeSolo
              options={cityOptions}
              value={bulkDeleteCity}
              onChange={(_, v) => {
                setBulkDeleteCity(v || null);
                setBulkDeletePreview(null);
                setBulkDeleteResult(null);
              }}
              onInputChange={(_, v) => setBulkDeleteCity(v || null)}
              renderInput={(p) => <TextField {...p} label="City" margin="normal" fullWidth />}
            />

            {bulkDeletePreview && !bulkDeleteResult && (
              <Alert
                severity={bulkDeletePreview.count === 0 ? 'info' : 'warning'}
                sx={{ mt: 2 }}
              >
                <Typography variant="subtitle2">
                  Up to {bulkDeletePreview.count} garden(s) will be deleted.
                </Typography>
                {bulkDeletePreview.sampleNames?.length > 0 && (
                  <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                    Sample: {bulkDeletePreview.sampleNames.join(', ')}
                    {bulkDeletePreview.count > bulkDeletePreview.sampleNames.length && ' …'}
                  </Typography>
                )}
                {bulkDeletePreview.count > 0 && (
                  <TextField
                    label='Type "DELETE" to confirm'
                    value={bulkDeleteConfirmText}
                    onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
                    fullWidth
                    margin="normal"
                    size="small"
                  />
                )}
              </Alert>
            )}

            {bulkDeleteResult && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Matched: ${bulkDeleteResult.matchedCount}`} />
                  <Chip color="success" label={`Deleted: ${bulkDeleteResult.deletedCount}`} />
                  <Chip color="warning" label={`Skipped: ${bulkDeleteResult.skipped?.length || 0}`} />
                </Stack>
                {bulkDeleteResult.skipped?.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        Skipped — could not delete ({bulkDeleteResult.skipped.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {bulkDeleteResult.skipped.map((s, i) => (
                        <Typography key={i} variant="body2">
                          #{s.gardenId} "{s.gardenName}" — {s.reason}
                        </Typography>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}

            {bulkDeleteError && <Alert severity="error" sx={{ mt: 2 }}>{bulkDeleteError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDelete} disabled={bulkDeleteLoading}>
            {bulkDeleteResult ? 'Close' : 'Cancel'}
          </Button>
          {!bulkDeleteResult && (
            <>
              <Button
                onClick={handlePreviewBulkDelete}
                disabled={bulkDeleteLoading || (!bulkDeleteCity && !bulkDeleteCountry)}
              >
                Preview
              </Button>
              <Button
                onClick={handleConfirmBulkDelete}
                variant="contained"
                color="error"
                disabled={
                  bulkDeleteLoading
                  || !bulkDeletePreview
                  || bulkDeletePreview.count === 0
                  || bulkDeleteConfirmText !== 'DELETE'
                }
                startIcon={bulkDeleteLoading ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
              >
                Confirm Delete
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Import JSON Dialog */}
      <Dialog open={importOpen} onClose={handleCloseImport} maxWidth="md" fullWidth>
        <DialogTitle>Import Gardens from JSON</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a country JSON file produced by the dog-park-finder app. Duplicates are
              skipped by stored OSM ID (idempotent re-imports) and by proximity to existing
              gardens. Imported gardens start hidden (visible=false) for admin review.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                Choose File
                <input type="file" hidden accept=".json,application/json" onChange={handleFileSelect} />
              </Button>
              {importFile && (
                <Typography variant="body2">
                  {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Stack>
            <TextField
              label="Conflict radius (meters)"
              type="number"
              value={importRadius}
              onChange={(e) => setImportRadius(Math.max(0, Number(e.target.value) || 0))}
              helperText="Skip an incoming park if any existing garden is within this distance. Default 100."
              fullWidth
              inputProps={{ min: 0, step: 10 }}
              sx={{ mb: 2 }}
            />
            {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
            {importResult && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Read: ${importResult.totalRead}`} />
                  <Chip color="success" label={`Imported: ${importResult.importedCount}`} />
                  <Chip color="default" label={`Dup OSM: ${importResult.skippedDuplicateOsmIds?.length || 0}`} />
                  <Chip color="warning" label={`Proximity: ${importResult.skippedProximityConflicts?.length || 0}`} />
                  <Chip color="error" label={`Invalid: ${importResult.skippedInvalid?.length || 0}`} />
                </Stack>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Skipped — duplicate OSM ID ({importResult.skippedDuplicateOsmIds?.length || 0})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {(importResult.skippedDuplicateOsmIds || []).map((d, i) => (
                      <Typography key={i} variant="body2">
                        osm_id={d.osmId}, name="{d.name}" → existing garden #{d.existingGardenId}
                      </Typography>
                    ))}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Skipped — proximity conflict ({importResult.skippedProximityConflicts?.length || 0})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {(importResult.skippedProximityConflicts || []).map((c, i) => (
                      <Typography key={i} variant="body2">
                        "{c.name}" (osm_id={c.osmId ?? '—'}) → conflicts with garden #{c.conflictingGardenId} "{c.conflictingGardenName}"
                      </Typography>
                    ))}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Skipped — invalid ({importResult.skippedInvalid?.length || 0})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {(importResult.skippedInvalid || []).map((e, i) => (
                      <Typography key={i} variant="body2">
                        osm_id={e.osmId ?? '—'}, name="{e.name ?? '—'}" — {e.reason}
                      </Typography>
                    ))}
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImport} disabled={importLoading}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmitImport}
            variant="contained"
            color="primary"
            disabled={importLoading || !importFile}
            startIcon={importLoading ? <CircularProgress size={16} /> : <UploadFileIcon />}
          >
            {importResult ? 'Import Again' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Removal Reports modal — read-only view of every user report against this garden. */}
      <Dialog
        open={!!reportsModalGarden}
        onClose={handleCloseRemovalReports}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Removal reports {reportsModalGarden ? `for "${reportsModalGarden.gardenName}"` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {reportsModalLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {reportsModalError && (
            <Alert severity="error" sx={{ mb: 2 }}>{reportsModalError}</Alert>
          )}
          {!reportsModalLoading && !reportsModalError && reportsModalReports.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No removal reports submitted for this garden.
            </Typography>
          )}
          {!reportsModalLoading && reportsModalReports.length > 0 && (
            <List>
              {reportsModalReports.map((r) => (
                <ListItem key={r.id} alignItems="flex-start" divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={r.reason}
                          color={r.status === 'OPEN' ? 'warning' : 'default'}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={r.status}
                          color={r.status === 'OPEN' ? 'warning' : 'success'}
                        />
                        <Typography variant="body2" color="text.secondary">
                          by {r.reporterName} (#{r.reporterId})
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <>
                        {r.note && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            "{r.note}"
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {new Date(r.createdAt).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemovalReports}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Dog Gardens Management - Connected to backend API
        </Typography>
      </Box>
    </Container>
  );
};

export default DogGardens;
